import { eloSelectionOpacity } from "./C1_eloSelectionFocus";
import { useRef } from "react";
import c1_state from "../model/C1_State";
import { useOnChangeWatcher } from "../../../src/models/useOnChangeWatcher";

const SIZE = 80;
const CENTER = SIZE / 2;
const OUTER = CENTER - 12;
const MARGIN = 16;

type NorthArrowControlProps = {
  angle: number;
  onAngleChange?: (angle: number) => void;
  onInteractionEnd?: () => void;
  transform?: string;
  opacity?: number;
};

/** The plan compass, also usable with a dialog draft without changing the project. */
export function C1_NorthArrowControl({ angle, onAngleChange, onInteractionEnd, transform, opacity }: NorthArrowControlProps) {
  const groupRef = useRef<SVGGElement>(null);
  const dragging = useRef(false);
  const interactive = !!onAngleChange;

  function angleFromPointer(clientX: number, clientY: number): number {
    const group = groupRef.current;
    if (!group) return angle;
    const screenMatrix = group.getScreenCTM();
    if (!screenMatrix) return angle;
    const point = new DOMPoint(clientX, clientY).matrixTransform(screenMatrix.inverse());
    const dx = point.x - CENTER;
    const dy = point.y - CENTER;
    const rad = Math.atan2(dx, -dy);
    return ((rad * 180) / Math.PI + 360) % 360;
  }

  function onPointerDown(event: React.PointerEvent<SVGGElement>) {
    if (!interactive) return;
    event.stopPropagation();
    dragging.current = true;
    groupRef.current?.setPointerCapture(event.pointerId);
    onAngleChange?.(angleFromPointer(event.clientX, event.clientY));
  }

  function onPointerMove(event: React.PointerEvent<SVGGElement>) {
    if (!dragging.current) return;
    onAngleChange?.(angleFromPointer(event.clientX, event.clientY));
  }

  function onPointerUp(event: React.PointerEvent<SVGGElement>) {
    if (!dragging.current) return;
    event.stopPropagation();
    dragging.current = false;
    onInteractionEnd?.();
  }

  const angleRad = (angle * Math.PI) / 180;
  const tipX = CENTER + OUTER * Math.sin(angleRad);
  const tipY = CENTER - OUTER * Math.cos(angleRad);
  const perpX = Math.cos(angleRad) * 6;
  const perpY = Math.sin(angleRad) * 6;
  const sTipX = CENTER - OUTER * Math.sin(angleRad);
  const sTipY = CENTER + OUTER * Math.cos(angleRad);
  const deg = Math.round(angle);

  return (
    <g
      ref={groupRef}
      className="C1_northArrowSvg"
      opacity={opacity}
      data-c1-svg-layer="north-arrow"
      transform={transform}
      style={{ cursor: interactive ? "default" : undefined, pointerEvents: interactive ? "all" : "none" }}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
    >
      <title>{`Nord: ${deg}°`}</title>
      <circle cx={CENTER} cy={CENTER} r={OUTER} fill="white" stroke="#94a3b8" strokeWidth={1.5} />

      <polygon points={`${CENTER},${CENTER} ${sTipX},${sTipY} ${CENTER - perpX},${CENTER - perpY}`} fill="#cbd5e1" />
      <polygon points={`${CENTER},${CENTER} ${sTipX},${sTipY} ${CENTER + perpX},${CENTER + perpY}`} fill="#94a3b8" />
      <polygon points={`${CENTER},${CENTER} ${tipX},${tipY} ${CENTER - perpX},${CENTER - perpY}`} fill="#1e293b" />
      <polygon points={`${CENTER},${CENTER} ${tipX},${tipY} ${CENTER + perpX},${CENTER + perpY}`} fill="#475569" />

      <circle cx={CENTER} cy={CENTER} r={3} fill="#1e293b" />
      <text
        x={tipX + (tipX - CENTER) * 0.28}
        y={tipY + (tipY - CENTER) * 0.28 + 4}
        textAnchor="middle"
        fontSize="10"
        fontWeight="bold"
        fill="#1e293b"
        pointerEvents="none"
      >
        N
      </text>
      <text
        x={CENTER}
        y={SIZE + 4}
        fill="#64748b"
        fontSize={10}
        textAnchor="middle"
        dominantBaseline="hanging"
        pointerEvents="none"
      >
        {deg}°
      </text>
    </g>
  );
}

/** Plan adapter keeps compass placement and export interaction unchanged. */
export function C1_NorthArrow() {
  useOnChangeWatcher(c1_state.project);
  const interactive = c1_state.floorImageRenderOptions.value === null;
  return <C1_NorthArrowControl
    angle={c1_state.project.northAngle}
    onAngleChange={interactive ? (angle) => c1_state.project.setNorthAngle(angle) : undefined}
    onInteractionEnd={() => state.undoManager.userInteractionEnded()}
    transform={`translate(${c1_state.svgCanvasWidth - SIZE - MARGIN} ${MARGIN})`}
    opacity={eloSelectionOpacity(undefined, c1_state)}
  />;
}
